import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO from '../../components/SEO';
import { getBlogPost, blogPosts } from '../../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const nextPost = blogPosts[currentIndex + 1] || null;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'Smart POS Africa',
      url: 'https://smartposafrica.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Smart POS Africa',
      logo: {
        '@type': 'ImageObject',
        url: 'https://smartposafrica.com/logo512.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://smartposafrica.com/blog/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        keywords={post.keywords}
        type="article"
        jsonLd={articleJsonLd}
      />
      <LandingNav />

      <article className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">

          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primaryB-400 hover:text-primaryB-700 text-sm font-semibold mb-8 transition-colors duration-200"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
            All articles
          </Link>

          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-primaryB-300 text-xs font-medium">{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-primaryB-200" />
              <span className="flex items-center gap-1 text-primaryB-300 text-xs font-medium">
                <Clock weight="bold" className="w-3 h-3" />
                {post.readTime}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 leading-tight mb-4">
              {post.title}
            </h1>
            <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed">
              {post.excerpt}
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-primaryB-200/60 mb-10" />

          {/* Content */}
          <div className="prose-custom space-y-5">
            {post.sections.map((section, i) => {
              if (section.type === 'h2') {
                return (
                  <h2 key={i} className="text-xl sm:text-2xl font-extrabold text-primaryB-900 mt-10 mb-3">
                    {section.content}
                  </h2>
                );
              }
              if (section.type === 'p') {
                return (
                  <p key={i} className="text-primaryB-600 text-base leading-relaxed">
                    {section.content}
                  </p>
                );
              }
              if (section.type === 'ul') {
                return (
                  <ul key={i} className="space-y-2.5 my-4">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-primaryB-600 text-base">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-14 bg-primaryB-900 rounded-2xl p-7 sm:p-10 text-center">
            <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-2">
              Ready to try Smart POS Africa?
            </h3>
            <p className="text-white/60 text-sm mb-6">
              One month free. No credit card. Setup in 5 minutes.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-3.5 rounded-full font-bold transition-all duration-200"
            >
              Start free trial
              <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </div>

          {/* Next article */}
          {nextPost && (
            <div className="mt-8">
              <p className="text-primaryB-300 text-xs font-bold tracking-widest uppercase mb-3">Next article</p>
              <Link
                to={`/blog/${nextPost.slug}`}
                className="group block bg-white border border-primaryB-200/60 rounded-2xl p-5 sm:p-6 hover:border-primaryB-300/80 hover:shadow-card transition-all duration-200"
              >
                <h4 className="font-extrabold text-primaryB-900 text-base group-hover:text-emerald-700 transition-colors duration-200 mb-1">
                  {nextPost.title}
                </h4>
                <p className="text-primaryB-400 text-sm">{nextPost.excerpt}</p>
              </Link>
            </div>
          )}
        </div>
      </article>

      <LandingFooter />
    </div>
  );
};

export default BlogPost;
